import { forwardRef } from "react";
import { getTemplate, SLIDE_DIMENSIONS, REEL_SAFE_ZONE, LogoBadge, SlideNumber } from "@/lib/carouselTemplates";

/**
 * Renders a single carousel slide at the template's native pixel dimensions
 * (1080×1350 for standard IG carousels, 1080×1920 for Reels). The
 * `previewScale` prop shrinks it visually via CSS transform while preserving
 * the full-resolution DOM node so html-to-image captures at full-res.
 *
 * Props:
 *   slide, brand, templateId, kind, meta, previewScale
 *   showSafeZones — draws dashed IG-UI safe-zone guides on top of the preview
 *                   (only visible in preview, never in the exported PNG)
 */
const CarouselSlide = forwardRef(function CarouselSlide(
  { slide, brand, templateId, kind, meta, previewScale = 1, showSafeZones = false },
  ref
) {
  // Custom templates live on the brand kit — pass them through so the
  // registry can resolve `custom-*` ids at render time.
  const template = getTemplate(templateId, brand?.custom_templates);
  const dims = template.dimensions || SLIDE_DIMENSIONS;
  const background = template.background(brand, kind);
  const content = template.layout(slide, brand, kind, meta);
  const dark = /^(#0|#1|linear-gradient\(.*(#0|#1))/i.test(background);
  const isReel = !!template.needsVideo;
  const photo = meta?.photo;
  const video = meta?.video;

  return (
    <div
      style={{
        width: dims.width * previewScale,
        height: dims.height * previewScale,
        overflow: "hidden",
        borderRadius: 12 * previewScale,
        boxShadow: "0 4px 20px rgba(0,0,0,0.08)",
        background: "#fff",
      }}
    >
      <div
        ref={ref}
        data-slide-kind={kind}
        style={{
          width: dims.width,
          height: dims.height,
          background,
          transform: `scale(${previewScale})`,
          transformOrigin: "top left",
          position: "relative",
          color: dark ? "#FFFFFF" : "#0A2342",
        }}
      >
        {/* Video preview layer (Reel templates only). The exported reel is
            composited server-side by FFmpeg — this <video> just gives the
            user a live preview of how text lands on their footage. */}
        {isReel && video && (
          <video
            src={video.url}
            muted playsInline loop autoPlay preload="metadata"
            style={{ position: "absolute", inset: 0, width: "100%", height: "100%", objectFit: "cover" }}
          />
        )}
        {content}
        {!isReel && <LogoBadge brand={brand} tone={dark ? "dark" : "light"} />}
        {!isReel && kind === "body" && (
          <SlideNumber index={meta.index} total={meta.total} brand={brand} tone={dark ? "dark" : "light"} />
        )}

        {/* Instagram UI safe-zone guides — only rendered in preview, not
            captured on export (html-to-image respects our `style` option
            which resets these guides via the exportHiddenSafeZones flag). */}
        {isReel && showSafeZones && (
          <div style={{ position: "absolute", inset: 0, pointerEvents: "none", zIndex: 50 }} data-reel-guides>
            {/* Top bar — username / more menu */}
            <div style={{ position: "absolute", top: 0, left: 0, right: 0, height: REEL_SAFE_ZONE.top, borderBottom: "2px dashed rgba(255,255,255,0.5)", background: "rgba(0,0,0,0.15)" }}>
              <div style={{ color: "white", fontSize: 20, padding: 20, fontWeight: 600 }}>↑ IG top bar</div>
            </div>
            {/* Bottom — caption & music */}
            <div style={{ position: "absolute", bottom: 0, left: 0, right: 0, height: REEL_SAFE_ZONE.bottom, borderTop: "2px dashed rgba(255,255,255,0.5)", background: "rgba(0,0,0,0.15)" }}>
              <div style={{ color: "white", fontSize: 20, padding: 20, fontWeight: 600 }}>↓ IG caption & music</div>
            </div>
            {/* Right — like/comment/share buttons */}
            <div style={{ position: "absolute", top: REEL_SAFE_ZONE.top, bottom: REEL_SAFE_ZONE.bottom, right: 0, width: REEL_SAFE_ZONE.right, borderLeft: "2px dashed rgba(255,255,255,0.5)", background: "rgba(0,0,0,0.15)" }}>
              <div style={{ color: "white", fontSize: 20, padding: 20, fontWeight: 600, writingMode: "vertical-rl" }}>→ IG action buttons</div>
            </div>
          </div>
        )}
      </div>
      {photo /* placeholder to silence unused-var lint if the linter kicks in */ && null}
    </div>
  );
});

export default CarouselSlide;
